/**
 * Reproductor del workshop: monta las pantallas REALES del Vault dentro de un
 * router en memoria y un `trpc.Provider` anidado que responde desde el Vault
 * de demo (lib/vault-demo). Encima, por portal al body: cursor guía, tarjeta
 * de capítulo, subtítulos, controles y cierre.
 *
 * Teclado: Espacio pausa/reanuda · → siguiente capítulo · Esc sale. Los
 * controles aparecen al mover el mouse y se esconden solos (para grabar).
 */
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import {
  MemoryRouter,
  Navigate,
  Route,
  Routes,
  UNSAFE_LocationContext,
  UNSAFE_NavigationContext,
  UNSAFE_RouteContext,
  useNavigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useReducedMotion } from 'framer-motion';
import { trpc } from '@/lib/trpc';
import { clearVaultUnlockToken } from '@/lib/vault-session';
import { applyDemoOp, createDemoState } from '@/lib/vault-demo/demo-vault';
import { createDemoTrpcClient } from '@/lib/vault-demo/demo-link';
import { VaultProtectedRoute } from '@/components/vault/VaultProtectedRoute';
import { VaultPage } from '@/pages/vault/VaultPage';
import { SecretDetailPage } from '@/pages/vault/SecretDetailPage';
import { GroupsPage } from '@/pages/vault/GroupsPage';
import { AuditPage } from '@/pages/vault/AuditPage';
import { WorkshopEngine } from './engine';
import { FakeCursor } from './FakeCursor';
import { ChapterCard } from './ChapterCard';
import { Subtitles } from './Subtitles';
import { PlayerControls } from './PlayerControls';
import { ClosingCard } from './ClosingCard';
import type { Workshop, WorkshopManifest, WorkshopSettings } from './types';

const ASSETS = `${import.meta.env.BASE_URL}vault-workshop/`;
const CONTROLS_IDLE_MS = 2500;

interface WorkshopPlayerProps {
  workshop: Workshop;
  manifest: WorkshopManifest;
  settings: WorkshopSettings;
  viewerEmail: string;
  startAt: number;
  onExit: () => void;
  /** "Volver a ver": el padre remonta el reproductor con un Vault de demo nuevo (el de esta vuelta ya está mutado). */
  onReplay: () => void;
}

/** Expone el `navigate` del router en memoria al motor (que no es React). */
function NavBridge({ navRef }: { navRef: React.MutableRefObject<(to: string) => void> }) {
  const navigate = useNavigate();
  useEffect(() => {
    navRef.current = (to) => navigate(to);
  }, [navigate, navRef]);
  return null;
}

// React Router prohíbe un <Router> adentro de otro (lo detecta por el
// LocationContext). El workshop necesita su propio router en memoria para que
// las pantallas reales del Vault naveguen entre sí sin salir de /vault/workshop:
// se vacían los tres contextos del router de la app justo alrededor del
// MemoryRouter, que así se cree de primer nivel. Son exports UNSAFE_ pero
// públicos y estables desde v6.
const NO_LOCATION = null as unknown as React.ContextType<typeof UNSAFE_LocationContext>;
const NO_NAVIGATION = null as unknown as React.ContextType<typeof UNSAFE_NavigationContext>;
const NO_ROUTE: React.ContextType<typeof UNSAFE_RouteContext> = { outlet: null, matches: [], isDataRoute: false };

function IsolatedRouter({ children }: { children: React.ReactNode }) {
  return (
    <UNSAFE_LocationContext.Provider value={NO_LOCATION}>
      <UNSAFE_NavigationContext.Provider value={NO_NAVIGATION}>
        <UNSAFE_RouteContext.Provider value={NO_ROUTE}>
          <MemoryRouter initialEntries={['/vault']}>{children}</MemoryRouter>
        </UNSAFE_RouteContext.Provider>
      </UNSAFE_NavigationContext.Provider>
    </UNSAFE_LocationContext.Provider>
  );
}

export function WorkshopPlayer({ workshop, manifest, settings, viewerEmail, startAt, onExit, onReplay }: WorkshopPlayerProps) {
  const realNavigate = useNavigate();
  const reduce = useReducedMotion();
  const navRef = useRef<(to: string) => void>(() => undefined);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  // el capítulo 1 arranca como persona nueva; el resto, con todo listo (lo fija cada `setup`)
  const demo = useMemo(() => createDemoState(viewerEmail, { fresh: startAt === 0 }), [viewerEmail, startAt]);
  const queryClient = useMemo(
    () => new QueryClient({ defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } } }),
    []
  );
  const client = useMemo(() => createDemoTrpcClient(demo), [demo]);

  const engine = useMemo(
    () =>
      new WorkshopEngine({
        workshop,
        manifest,
        settings: () => settingsRef.current,
        navigate: (to) => navRef.current(to),
        applyDemo: (op) => applyDemoOp(demo, op),
        queryClient,
        audioUrl: (id) => `${ASSETS}audio/${id}.mp3`,
        musicUrl: `${ASSETS}music.mp3`,
        reduceMotion: !!reduce,
      }),
    // el motor vive lo que vive el reproductor; cambios de settings entran por el ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workshop, manifest, demo, queryClient]
  );
  const state = useSyncExternalStore(engine.subscribe, engine.getState);

  useEffect(() => {
    engine.start(startAt);
    return () => {
      engine.destroy();
      // el token de demo nunca debe sobrevivir al workshop (viajaría al Vault real)
      clearVaultUnlockToken();
    };
  }, [engine, startAt]);

  useEffect(() => {
    engine.applySettings();
  }, [engine, settings]);

  // teclado
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // solo teclas de la persona: el motor despacha Escape sintético para cerrar
      // diálogos de Radix (dom.ts pressKey) y eso NO es "salir del workshop"
      if (!e.isTrusted) return;
      if (e.key === ' ') {
        e.preventDefault();
        engine.togglePause();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        engine.nextChapter();
      } else if (e.key === 'Escape') {
        onExit();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [engine, onExit]);

  // controles al mover el mouse
  const [controls, setControls] = useState(true);
  useEffect(() => {
    let timer = window.setTimeout(() => setControls(false), CONTROLS_IDLE_MS);
    const onMove = () => {
      setControls(true);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setControls(false), CONTROLS_IDLE_MS);
    };
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.clearTimeout(timer);
    };
  }, []);

  const chapter = workshop.chapters[state.chapter];

  return (
    <div className="relative">
      <trpc.Provider client={client} queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <IsolatedRouter>
            <NavBridge navRef={navRef} />
            <Routes>
              <Route element={<VaultProtectedRoute />}>
                <Route path="/vault" element={<VaultPage />} />
                <Route path="/vault/secrets/:secretId" element={<SecretDetailPage />} />
                <Route path="/vault/groups" element={<GroupsPage />} />
                <Route path="/vault/audit" element={<AuditPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/vault" replace />} />
            </Routes>
          </IsolatedRouter>
        </QueryClientProvider>
      </trpc.Provider>

      {createPortal(
        <>
          {/* Escudo: mientras el tour corre, los clics de la persona no llegan a la
              app (los del cursor guía se despachan por JS y pasan igual). Los
              controles, el cierre y el cursor están por encima. */}
          {state.status !== 'ended' && <div aria-hidden className="fixed inset-0 z-202" />}
          <PlayerControls
            state={state}
            chapterTitle={chapter?.title ?? ''}
            chapters={workshop.chapters.length}
            visible={controls}
            onTogglePause={() => engine.togglePause()}
            onNext={() => engine.nextChapter()}
            onExit={onExit}
          />
          <ChapterCard card={state.card} />
          <Subtitles text={state.status === 'ended' ? null : state.subtitle} />
          <ClosingCard
            rules={workshop.rules}
            show={state.overlay === 'rules' || state.status === 'ended'}
            ended={state.status === 'ended'}
            onReplay={onReplay}
            onGoVault={() => {
              // explícito además del cleanup del useEffect: el token de demo no debe
              // llegar al Vault real ni por un instante
              clearVaultUnlockToken();
              onExit();
              realNavigate('/vault');
            }}
            onExit={onExit}
          />
          <FakeCursor cursor={state.cursor} />
        </>,
        document.body
      )}
    </div>
  );
}
